import React, { useMemo, useState } from 'react';
import { CreditCard, Search, Filter, Download, Eye, RefreshCw, CheckCircle2, Clock, XCircle, AlertTriangle, Pause, Play, MoreVertical, Gift, ArrowUpRight, Users, Star, Activity, FileText, ChevronRight, X, Settings, BarChart3, ShieldAlert, Zap, Calendar, DollarSign, ArrowRight, Check, AlertOctagon, RefreshCcw, User, Plus } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { mockSubscriptions, mockTariffPlans, mockAnalytics } from './subscriptions/subscriptionMockData';
import { Subscription, SubscriptionType, SubscriptionPlan, SubscriptionStatus, PaymentError } from './subscriptions/subscriptionTypes';
interface AdminSubscriptionsFullManagerProps {
  userSubscriptions: any[];
  users: any[];
  setActiveTab: (tab: string) => void;
  success: (msg: string) => void;
  onCancelSubscription?: (id: number) => void;
}
const planLabels: Record<SubscriptionPlan, string> = {
  basic: 'Basic',
  pro: 'Pro',
  premium: 'Premium',
  reports_bundle: 'Отчеты',
  courses_access: 'Курсы',
  vip_consultations: 'VIP Консультации'
};
const statusConfig: Record<SubscriptionStatus, {
  label: string;
  variant: 'success' | 'warning' | 'error' | 'neutral' | 'pro';
}> = {
  active: {
    label: 'Активна',
    variant: 'success'
  },
  trial: {
    label: 'Пробная',
    variant: 'pro'
  },
  paused: {
    label: 'Пауза',
    variant: 'warning'
  },
  cancelled: {
    label: 'Отменена',
    variant: 'neutral'
  },
  expired: {
    label: 'Истекла',
    variant: 'neutral'
  },
  payment_error: {
    label: 'Ошибка оплаты',
    variant: 'error'
  }
};
export function AdminSubscriptionsFullManager({
  success,
  onCancelSubscription
}: AdminSubscriptionsFullManagerProps) {
  const [activeView, setActiveView] = useState<'list' | 'errors' | 'plans' | 'analytics'>('list');
  const [subscriptions, setSubscriptions] = useState<Subscription[]>(mockSubscriptions);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSubId, setSelectedSubId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  // Filters state
  const [filters, setFilters] = useState({
    type: 'all' as SubscriptionType | 'all',
    plan: 'all' as SubscriptionPlan | 'all',
    status: 'all' as SubscriptionStatus | 'all',
    autoRenew: 'all' as 'true' | 'false' | 'all',
    source: 'all'
  });
  // Top metric quick filter
  const [metricFilter, setMetricFilter] = useState<string | null>(null);
  // Derived metrics
  const metrics = useMemo(() => {
    return {
      total: subscriptions.length,
      active: subscriptions.filter(s => s.status === 'active').length,
      trial: subscriptions.filter(s => s.status === 'trial').length,
      cancelled: subscriptions.filter(s => s.status === 'cancelled').length,
      errors: subscriptions.filter(s => s.status === 'payment_error').length,
      mrr: subscriptions.filter(s => s.status === 'active' || s.status === 'trial').reduce((acc, s) => acc + (s.billingPeriod === 'monthly' ? s.price : s.price / 12), 0),
      newThisMonth: 14,
      renewals: 156 // Mock
    };
  }, [subscriptions]);
  // Filtering logic
  const filteredSubs = useMemo(() => {
    return subscriptions.filter(s => {
      // Search
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!s.userName.toLowerCase().includes(q) && !s.id.toLowerCase().includes(q) && !s.userEmail.toLowerCase().includes(q)) {
          return false;
        }
      }
      // Metric quick filters
      if (metricFilter === 'active' && s.status !== 'active') return false;
      if (metricFilter === 'trial' && s.status !== 'trial') return false;
      if (metricFilter === 'cancelled' && s.status !== 'cancelled') return false;
      if (metricFilter === 'errors' && s.status !== 'payment_error') return false;
      // Advanced filters
      if (filters.type !== 'all' && s.type !== filters.type) return false;
      if (filters.plan !== 'all' && s.plan !== filters.plan) return false;
      if (filters.status !== 'all' && s.status !== filters.status) return false;
      if (filters.autoRenew !== 'all') {
        const isAuto = s.autoRenew ? 'true' : 'false';
        if (filters.autoRenew !== isAuto) return false;
      }
      if (filters.source !== 'all' && s.source !== filters.source) return false;
      return true;
    }).sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [subscriptions, searchQuery, filters, metricFilter]);
  const paginatedSubs = filteredSubs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredSubs.length / itemsPerPage);
  const selectedSub = useMemo(() => subscriptions.find(s => s.id === selectedSubId), [subscriptions, selectedSubId]);
  // Actions
  const handleAction = (id: string, action: string) => {
    setSubscriptions(prev => prev.map(s => {
      if (s.id !== id) return s;
      const newEvent = {
        id: `EVT-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        type: 'system' as any,
        description: `Действие: ${action}`,
        actor: 'admin' as const
      };
      let updates: Partial<Subscription> = {
        history: [newEvent, ...s.history]
      };
      switch (action) {
        case 'cancel':
          updates.status = 'cancelled';
          updates.autoRenew = false;
          if (onCancelSubscription) onCancelSubscription(parseInt(id.split('-')[1]));
          break;
        case 'pause':
          updates.status = 'paused';
          break;
        case 'resume':
          updates.status = 'active';
          break;
        case 'toggle_auto_renew':
          updates.autoRenew = !s.autoRenew;
          break;
        case 'manual_renew':
          updates.endDate = new Date(new Date(s.endDate).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          break;
      }
      return {
        ...s,
        ...updates
      };
    }));
    success(`Действие "${action}" выполнено для ${id}`);
  };
  const handleExportCSV = () => {
    success(`Экспорт ${filteredSubs.length} записей начат`);
  };
  // Renderers
  const renderKpi = (key: string, label: string, value: string | number, icon: React.ReactNode, bgClass: string, textClass: string) => <Card padding="sm" hoverable className={`rounded-xl shadow-none border transition-all cursor-pointer group ${metricFilter === key ? 'border-indigo-400 ring-1 ring-indigo-400 bg-indigo-50/30' : 'border-slate-200 bg-white'}`} onClick={() => {
    setMetricFilter(metricFilter === key ? null : key);
    setCurrentPage(1);
  }} data-id="element-6709">
      <div className="flex items-center justify-between mb-2" data-id="element-6710">
        <div className={`w-8 h-8 rounded-lg ${bgClass} flex items-center justify-center group-hover:scale-110 transition-transform`} data-id="element-6711">
          {icon}
        </div>
        {metricFilter === key && <CheckCircle2 className="w-4 h-4 text-indigo-500" data-id="element-6712" />}
      </div>
      <div data-id="element-6713">
        <div className={`text-xl font-bold ${textClass}`} data-id="element-6714">{value}</div>
        <div className="text-[10px] text-slate-500 font-medium uppercase tracking-wider mt-0.5" data-id="element-6715">
          {label}
        </div>
      </div>
    </Card>;
  const renderRightPanel = () => {
    if (!selectedSub) return null;
    return <div className="fixed inset-y-0 right-0 w-full sm:w-[400px] bg-white shadow-2xl border-l border-slate-200 z-50 flex flex-col transform transition-transform duration-300" data-id="element-6716">
        <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50" data-id="element-6717">
          <div data-id="element-6718">
            <div className="text-xs font-mono text-slate-500 mb-1" data-id="element-6719">
              {selectedSub.id}
            </div>
            <h3 className="text-lg font-bold text-slate-900" data-id="element-6720">
              {selectedSub.userName}
            </h3>
          </div>
          <button onClick={() => setSelectedSubId(null)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-6721">
            <X className="w-5 h-5" data-id="element-6722" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-6" data-id="element-6723">
          {/* Status & Plan Card */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-id="element-6724">
            <div className="flex items-center justify-between mb-4" data-id="element-6725">
              <Badge variant={statusConfig[selectedSub.status].variant} data-id="element-6726">
                {statusConfig[selectedSub.status].label}
              </Badge>
              <div className="text-sm font-bold text-slate-900" data-id="element-6727">
                {selectedSub.price} {selectedSub.currency} /{' '}
                {selectedSub.billingPeriod === 'monthly' ? 'мес' : 'год'}
              </div>
            </div>
            <div className="space-y-3" data-id="element-6728">
              <div className="flex justify-between text-sm" data-id="element-6729">
                <span className="text-slate-500" data-id="element-6730">План</span>
                <span className="font-medium text-slate-900" data-id="element-6731">
                  {planLabels[selectedSub.plan]}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-6732">
                <span className="text-slate-500" data-id="element-6733">Тип</span>
                <span className="font-medium text-slate-900 flex items-center gap-1" data-id="element-6734">
                  {selectedSub.type === 'astrologer' ? <Star className="w-3 h-3 text-amber-500" data-id="element-6735" /> : <User className="w-3 h-3 text-blue-500" data-id="element-6736" />}
                  {selectedSub.type === 'astrologer' ? 'Астролог' : 'Пользователь'}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-6737">
                <span className="text-slate-500" data-id="element-6738">Период</span>
                <span className="font-medium text-slate-900" data-id="element-6739">
                  {selectedSub.startDate} — {selectedSub.endDate}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-6740">
                <span className="text-slate-500" data-id="element-6741">Автопродление</span>
                <span className="font-medium text-slate-900 flex items-center gap-1" data-id="element-6742">
                  {selectedSub.autoRenew ? <Check className="w-3 h-3 text-emerald-500" data-id="element-6743" /> : <X className="w-3 h-3 text-rose-500" data-id="element-6744" />}
                  {selectedSub.autoRenew ? 'Включено' : 'Выключено'}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div data-id="element-6745">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-6746">
              Быстрые действия
            </h4>
            <div className="grid grid-cols-2 gap-2" data-id="element-6747">
              <Button variant="outline" size="sm" className="justify-start text-xs" onClick={() => handleAction(selectedSub.id, 'change_plan')} data-id="element-6748">
                <RefreshCw className="w-3.5 h-3.5 mr-2 text-slate-400" data-id="element-6749" />{' '}
                Изменить план
              </Button>
              <Button variant="outline" size="sm" className="justify-start text-xs" onClick={() => handleAction(selectedSub.id, 'manual_renew')} data-id="element-6750">
                <Calendar className="w-3.5 h-3.5 mr-2 text-slate-400" data-id="element-6751" />{' '}
                Продлить вручную
              </Button>
              <Button variant="outline" size="sm" className="justify-start text-xs" onClick={() => handleAction(selectedSub.id, 'toggle_auto_renew')} data-id="element-6752">
                <RefreshCcw className="w-3.5 h-3.5 mr-2 text-slate-400" data-id="element-6753" />{' '}
                {selectedSub.autoRenew ? 'Откл. автоплатеж' : 'Вкл. автоплатеж'}
              </Button>
              {selectedSub.status === 'active' ? <Button variant="outline" size="sm" className="justify-start text-xs" onClick={() => handleAction(selectedSub.id, 'pause')} data-id="element-6754">
                  <Pause className="w-3.5 h-3.5 mr-2 text-amber-500" data-id="element-6755" /> Пауза
                </Button> : selectedSub.status === 'paused' ? <Button variant="outline" size="sm" className="justify-start text-xs" onClick={() => handleAction(selectedSub.id, 'resume')} data-id="element-6756">
                  <Play className="w-3.5 h-3.5 mr-2 text-emerald-500" data-id="element-6757" />{' '}
                  Возобновить
                </Button> : null}
              <Button variant="outline" size="sm" className="justify-start text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => handleAction(selectedSub.id, 'cancel')} data-id="element-6758">
                <XCircle className="w-3.5 h-3.5 mr-2" data-id="element-6759" /> Отменить
              </Button>
              <Button variant="outline" size="sm" className="justify-start text-xs text-amber-600 hover:bg-amber-50 hover:text-amber-700" onClick={() => handleAction(selectedSub.id, 'refund')} data-id="element-6760">
                <DollarSign className="w-3.5 h-3.5 mr-2" data-id="element-6761" /> Возврат
              </Button>
            </div>
          </div>

          {/* Payment Errors (if any) */}
          {selectedSub.activeErrors.length > 0 && <div data-id="element-6762">
              <h4 className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-3 flex items-center gap-1" data-id="element-6763">
                <AlertTriangle className="w-4 h-4" data-id="element-6764" /> Ошибки оплаты
              </h4>
              <div className="space-y-2" data-id="element-6765">
                {selectedSub.activeErrors.map(err => <div key={err.id} className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm" data-id="element-6766">
                    <div className="flex justify-between font-medium text-rose-900 mb-1" data-id="element-6767">
                      <span data-id="element-6768">
                        {err.amount} {selectedSub.currency}
                      </span>
                      <span data-id="element-6769">{err.date}</span>
                    </div>
                    <div className="text-rose-700 text-xs mb-2" data-id="element-6770">
                      Причина: {err.reason} (Попыток: {err.attempts})
                    </div>
                    <Button variant="primary" size="sm" className="w-full bg-rose-600 hover:bg-rose-700 text-xs py-1 h-auto" onClick={() => success('Запущен повторный биллинг')} data-id="element-6771">
                      Повторить списание
                    </Button>
                  </div>)}
              </div>
            </div>}

          {/* History */}
          <div data-id="element-6772">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-6773">
              История
            </h4>
            <div className="relative border-l-2 border-slate-100 ml-2 space-y-4 pb-4" data-id="element-6774">
              {selectedSub.history.map((evt, i) => <div key={evt.id} className="relative pl-4" data-id="element-6775">
                  <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white ${evt.type === 'payment_failed' ? 'bg-rose-500' : evt.type === 'cancelled' ? 'bg-slate-400' : 'bg-indigo-500'}`} data-id="element-6776" />
                  <div className="text-xs text-slate-500 mb-0.5" data-id="element-6777">
                    {evt.date} • {evt.actor}
                  </div>
                  <div className="text-sm font-medium text-slate-900" data-id="element-6778">
                    {evt.description}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>;
  };
  const renderList = () => <div className="space-y-4" data-id="element-6779">
      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-id="element-6780">
        {renderKpi('total', 'Всего', metrics.total, <CreditCard className="w-4 h-4 text-indigo-600" data-id="element-6781" />, 'bg-indigo-50', 'text-indigo-900')}
        {renderKpi('active', 'Активные', metrics.active, <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-6782" />, 'bg-emerald-50', 'text-emerald-900')}
        {renderKpi('trial', 'Пробные', metrics.trial, <Play className="w-4 h-4 text-blue-600" data-id="element-6783" />, 'bg-blue-50', 'text-blue-900')}
        {renderKpi('cancelled', 'Отмененные', metrics.cancelled, <XCircle className="w-4 h-4 text-slate-600" data-id="element-6784" />, 'bg-slate-100', 'text-slate-900')}
        {renderKpi('errors', 'Ошибки', metrics.errors, <AlertTriangle className="w-4 h-4 text-rose-600" data-id="element-6785" />, 'bg-rose-50', 'text-rose-900')}
        {renderKpi('mrr', 'MRR', `₽${Math.floor(metrics.mrr / 1000)}K`, <ArrowUpRight className="w-4 h-4 text-emerald-600" data-id="element-6786" />, 'bg-emerald-50', 'text-emerald-900')}
        {renderKpi('new', 'Новые (мес)', metrics.newThisMonth, <Star className="w-4 h-4 text-amber-600" data-id="element-6787" />, 'bg-amber-50', 'text-amber-900')}
        {renderKpi('renewals', 'Продления', metrics.renewals, <RefreshCw className="w-4 h-4 text-purple-600" data-id="element-6788" />, 'bg-purple-50', 'text-purple-900')}
      </div>

      {/* Toolbar */}
      <Card padding="sm" className="rounded-xl shadow-none border-slate-200" data-id="element-6789">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-6790">
          <div className="relative flex-1 max-w-md w-full" data-id="element-6791">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-6792" />
            <input type="text" placeholder="Поиск по ID, имени, email..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-6793" />
          </div>
          <div className="flex gap-2 w-full sm:w-auto" data-id="element-6794">
            <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Filter className="w-4 h-4" data-id="element-6796" />} onClick={() => setShowFilters(!showFilters)} className="flex-1 sm:flex-none" data-id="element-6795">
              Фильтры
            </Button>
            <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-6798" />} onClick={handleExportCSV} className="flex-1 sm:flex-none" data-id="element-6797">
              Экспорт
            </Button>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3" data-id="element-6799">
            <div data-id="element-6800">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6801">
                Тип
              </label>
              <select className="w-full rounded-lg border border-slate-200 text-sm py-1.5 px-2" value={filters.type} onChange={e => setFilters({
            ...filters,
            type: e.target.value as any
          })} data-id="element-6802">
                <option value="all" data-id="element-6803">Все типы</option>
                <option value="astrologer" data-id="element-6804">Астрологи</option>
                <option value="user" data-id="element-6805">Пользователи</option>
              </select>
            </div>
            <div data-id="element-6806">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6807">
                План
              </label>
              <select className="w-full rounded-lg border border-slate-200 text-sm py-1.5 px-2" value={filters.plan} onChange={e => setFilters({
            ...filters,
            plan: e.target.value as any
          })} data-id="element-6808">
                <option value="all" data-id="element-6809">Все планы</option>
                {Object.entries(planLabels).map(([k, v]) => <option key={k} value={k} data-id="element-6810">
                    {v}
                  </option>)}
              </select>
            </div>
            <div data-id="element-6811">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6812">
                Статус
              </label>
              <select className="w-full rounded-lg border border-slate-200 text-sm py-1.5 px-2" value={filters.status} onChange={e => setFilters({
            ...filters,
            status: e.target.value as any
          })} data-id="element-6813">
                <option value="all" data-id="element-6814">Все статусы</option>
                {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k} data-id="element-6815">
                    {v.label}
                  </option>)}
              </select>
            </div>
            <div data-id="element-6816">
              <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6817">
                Автоплатеж
              </label>
              <select className="w-full rounded-lg border border-slate-200 text-sm py-1.5 px-2" value={filters.autoRenew} onChange={e => setFilters({
            ...filters,
            autoRenew: e.target.value as any
          })} data-id="element-6818">
                <option value="all" data-id="element-6819">Все</option>
                <option value="true" data-id="element-6820">Включен</option>
                <option value="false" data-id="element-6821">Выключен</option>
              </select>
            </div>
            <div className="flex items-end" data-id="element-6822">
              <Button variant="ghost" size="sm" className="w-full text-slate-500" onClick={() => setFilters({
            type: 'all',
            plan: 'all',
            status: 'all',
            autoRenew: 'all',
            source: 'all'
          })} data-id="element-6823">
                Сбросить
              </Button>
            </div>
          </div>}
      </Card>

      {/* Table */}
      <Card padding="none" className="rounded-xl shadow-none border-slate-200 overflow-hidden" data-id="element-6824">
        <div className="overflow-x-auto" data-id="element-6825">
          <table className="w-full text-left whitespace-nowrap" data-id="element-6826">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-6827">
              <tr data-id="element-6828">
                <th className="px-4 py-3 font-medium" data-id="element-6829">ID / Пользователь</th>
                <th className="px-4 py-3 font-medium" data-id="element-6830">Тип / План</th>
                <th className="px-4 py-3 font-medium" data-id="element-6831">Цена</th>
                <th className="px-4 py-3 font-medium" data-id="element-6832">Период</th>
                <th className="px-4 py-3 font-medium" data-id="element-6833">Сл. списание</th>
                <th className="px-4 py-3 font-medium" data-id="element-6834">Авто</th>
                <th className="px-4 py-3 font-medium" data-id="element-6835">Статус</th>
                <th className="px-4 py-3 font-medium" data-id="element-6836">LTV / Оплаты</th>
                <th className="px-4 py-3 text-right font-medium" data-id="element-6837">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-6838">
              {paginatedSubs.length === 0 ? <tr data-id="element-6839">
                  <td colSpan={9} className="px-4 py-8 text-center text-slate-500" data-id="element-6840">
                    <div className="flex flex-col items-center justify-center" data-id="element-6841">
                      <Search className="w-8 h-8 text-slate-300 mb-2" data-id="element-6842" />
                      <p data-id="element-6843">Подписки не найдены</p>
                    </div>
                  </td>
                </tr> : paginatedSubs.map(s => <tr key={s.id} className={`hover:bg-slate-50 transition-colors cursor-pointer ${selectedSubId === s.id ? 'bg-indigo-50/50' : ''} ${s.status === 'payment_error' ? 'bg-rose-50/30' : ''}`} onClick={() => setSelectedSubId(s.id)} data-id="element-6844">
                    <td className="px-4 py-3" data-id="element-6845">
                      <div className="text-xs font-mono text-slate-500 mb-0.5" data-id="element-6846">
                        {s.id}
                      </div>
                      <div className="text-sm font-bold text-slate-900" data-id="element-6847">
                        {s.userName}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-6848">
                      <div className="flex items-center gap-1.5 mb-1" data-id="element-6849">
                        {s.type === 'astrologer' ? <Star className="w-3 h-3 text-amber-500" data-id="element-6850" /> : <User className="w-3 h-3 text-blue-500" data-id="element-6851" />}
                        <span className="text-xs text-slate-600" data-id="element-6852">
                          {s.type === 'astrologer' ? 'Астролог' : 'Юзер'}
                        </span>
                      </div>
                      <Badge variant="default" size="sm" data-id="element-6853">
                        {planLabels[s.plan]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3" data-id="element-6854">
                      <div className="text-sm font-medium text-slate-900" data-id="element-6855">
                        {s.price} {s.currency}
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-6856">
                        {s.billingPeriod === 'monthly' ? '/ мес' : '/ год'}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-6857">
                      <div className="text-xs text-slate-900" data-id="element-6858">
                        {s.startDate}
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-6859">
                        до {s.endDate}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600" data-id="element-6860">
                      {s.nextChargeDate || '—'}
                    </td>
                    <td className="px-4 py-3" data-id="element-6861">
                      {s.autoRenew ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-6862" /> : <XCircle className="w-4 h-4 text-slate-300" data-id="element-6863" />}
                    </td>
                    <td className="px-4 py-3" data-id="element-6864">
                      <Badge variant={statusConfig[s.status].variant} size="sm" data-id="element-6865">
                        {statusConfig[s.status].label}
                      </Badge>
                      {s.activeErrors.length > 0 && <div className="text-[10px] text-rose-600 mt-1 flex items-center gap-1" data-id="element-6866">
                          <AlertTriangle className="w-3 h-3" data-id="element-6867" />{' '}
                          {s.activeErrors.length} ошиб.
                        </div>}
                    </td>
                    <td className="px-4 py-3" data-id="element-6868">
                      <div className="text-sm font-medium text-slate-900" data-id="element-6869">
                        {s.ltv.toLocaleString()} {s.currency}
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-6870">
                        {s.paymentsCount} успешных
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-6871">
                      <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-slate-400 hover:text-indigo-600" onClick={e => {
                  e.stopPropagation();
                  setSelectedSubId(s.id);
                }} data-id="element-6872">
                        <MoreVertical className="w-4 h-4" data-id="element-6873" />
                      </Button>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex justify-center" data-id="element-6874">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-6875" />
          </div>}
      </Card>
    </div>;
  const renderErrors = () => {
    const errorSubs = subscriptions.filter(s => s.activeErrors.length > 0);
    return <div className="space-y-4" data-id="element-6876">
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3" data-id="element-6877">
          <AlertOctagon className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" data-id="element-6878" />
          <div data-id="element-6879">
            <h3 className="text-sm font-bold text-rose-900" data-id="element-6880">
              Ошибки списаний ({errorSubs.length})
            </h3>
            <p className="text-xs text-rose-700 mt-1" data-id="element-6881">
              Подписки с неудачными попытками списания средств. Система
              автоматически повторяет попытки согласно расписанию.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-6882">
          {errorSubs.map(s => <Card key={s.id} padding="sm" className="border-rose-200 shadow-sm relative overflow-hidden" data-id="element-6883">
              <div className="absolute top-0 left-0 w-1 h-full bg-rose-500" data-id="element-6884" />
              <div className="flex justify-between items-start mb-3" data-id="element-6885">
                <div data-id="element-6886">
                  <div className="text-sm font-bold text-slate-900" data-id="element-6887">
                    {s.userName}
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-6888">
                    {s.id} • {planLabels[s.plan]}
                  </div>
                </div>
                <Badge variant="error" size="sm" data-id="element-6889">
                  {s.price} {s.currency}
                </Badge>
              </div>

              <div className="space-y-2 mb-4" data-id="element-6890">
                {s.activeErrors.map(err => <div key={err.id} className="bg-slate-50 rounded p-2 text-xs" data-id="element-6891">
                    <div className="flex justify-between text-slate-700 mb-1" data-id="element-6892">
                      <span data-id="element-6893">Дата: {err.date}</span>
                      <span className="font-medium text-rose-600" data-id="element-6894">
                        Попыток: {err.attempts}
                      </span>
                    </div>
                    <div className="text-slate-600" data-id="element-6895">
                      Причина: <span className="font-medium" data-id="element-6896">{err.reason}</span>
                    </div>
                    {err.nextAttemptDate && <div className="text-slate-500 mt-1" data-id="element-6897">
                        След. попытка: {err.nextAttemptDate}
                      </div>}
                  </div>)}
              </div>

              <div className="flex gap-2" data-id="element-6898">
                <Button variant="primary" size="sm" className="flex-1 bg-rose-600 hover:bg-rose-700 text-xs py-1.5 h-auto" onClick={() => success('Запущен повторный биллинг')} data-id="element-6899">
                  Повторить списание
                </Button>
                <Button variant="outline" size="sm" className="flex-1 text-xs py-1.5 h-auto" onClick={() => {
              setSelectedSubId(s.id);
              setActiveView('list');
            }} data-id="element-6900">
                  Детали
                </Button>
              </div>
            </Card>)}
          {errorSubs.length === 0 && <div className="col-span-full py-12 text-center text-slate-500" data-id="element-6901">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" data-id="element-6902" />
              <p className="text-lg font-medium text-slate-900" data-id="element-6903">
                Нет ошибок оплаты
              </p>
              <p className="text-sm mt-1" data-id="element-6904">
                Все автоматические списания проходят успешно.
              </p>
            </div>}
        </div>
      </div>;
  };
  const renderPlans = () => <div className="space-y-6" data-id="element-6905">
      <div className="flex justify-between items-center" data-id="element-6906">
        <h3 className="text-lg font-bold text-slate-900" data-id="element-6907">Тарифные планы</h3>
        <Button variant="primary" size="sm" icon={<Plus className="w-4 h-4" data-id="element-6909" />} onClick={() => success('Открыта форма создания тарифа')} data-id="element-6908">
          Создать тариф
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-6910">
        {/* Astrologer Plans */}
        <div data-id="element-6911">
          <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-6912">
            <Star className="w-4 h-4 text-amber-500" data-id="element-6913" /> Для астрологов
          </h4>
          <div className="space-y-4" data-id="element-6914">
            {mockTariffPlans.filter(p => p.type === 'astrologer').map(plan => <Card key={plan.id} padding="sm" className="border-slate-200" data-id="element-6915">
                  <div className="flex justify-between items-start mb-2" data-id="element-6916">
                    <div data-id="element-6917">
                      <div className="flex items-center gap-2" data-id="element-6918">
                        <h5 className="font-bold text-slate-900" data-id="element-6919">
                          {plan.name}
                        </h5>
                        {!plan.isActive && <Badge variant="neutral" size="sm" data-id="element-6920">
                            Архив
                          </Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5" data-id="element-6921">
                        {plan.description}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-6922">
                      <div className="font-bold text-indigo-600" data-id="element-6923">
                        {plan.priceMonthly} {plan.currency}/мес
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-6924">
                        {plan.priceYearly} {plan.currency}/год
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-1" data-id="element-6925">
                    {plan.features.map((f, i) => <div key={i} className="flex items-center gap-2 text-xs text-slate-600" data-id="element-6926">
                        <Check className="w-3 h-3 text-emerald-500" data-id="element-6927" /> {f}
                      </div>)}
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center" data-id="element-6928">
                    <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-1 rounded" data-id="element-6929">
                      Триал: {plan.trialDays} дней
                    </span>
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => success('Редактирование тарифа')} data-id="element-6930">
                      Настроить
                    </Button>
                  </div>
                </Card>)}
          </div>
        </div>

        {/* User Plans */}
        <div data-id="element-6931">
          <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-6932">
            <User className="w-4 h-4 text-blue-500" data-id="element-6933" /> Для пользователей
          </h4>
          <div className="space-y-4" data-id="element-6934">
            {mockTariffPlans.filter(p => p.type === 'user').map(plan => <Card key={plan.id} padding="sm" className="border-slate-200" data-id="element-6935">
                  <div className="flex justify-between items-start mb-2" data-id="element-6936">
                    <div data-id="element-6937">
                      <div className="flex items-center gap-2" data-id="element-6938">
                        <h5 className="font-bold text-slate-900" data-id="element-6939">
                          {plan.name}
                        </h5>
                        {!plan.isActive && <Badge variant="neutral" size="sm" data-id="element-6940">
                            Архив
                          </Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5" data-id="element-6941">
                        {plan.description}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-6942">
                      <div className="font-bold text-indigo-600" data-id="element-6943">
                        {plan.priceMonthly} {plan.currency}/мес
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-6944">
                        {plan.priceYearly} {plan.currency}/год
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-1" data-id="element-6945">
                    {plan.features.map((f, i) => <div key={i} className="flex items-center gap-2 text-xs text-slate-600" data-id="element-6946">
                        <Check className="w-3 h-3 text-emerald-500" data-id="element-6947" /> {f}
                      </div>)}
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center" data-id="element-6948">
                    <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-1 rounded" data-id="element-6949">
                      Триал: {plan.trialDays} дней
                    </span>
                    <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => success('Редактирование тарифа')} data-id="element-6950">
                      Настроить
                    </Button>
                  </div>
                </Card>)}
          </div>
        </div>
      </div>
    </div>;
  const renderAnalytics = () => <div className="space-y-6" data-id="element-6951">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6952">
        <Card padding="sm" className="border-slate-200" data-id="element-6953">
          <div className="text-sm text-slate-500 mb-1" data-id="element-6954">
            Удержание (Retention)
          </div>
          <div className="text-3xl font-bold text-emerald-600" data-id="element-6955">
            {mockAnalytics.retentionRate}%
          </div>
          <div className="text-xs text-emerald-600 flex items-center gap-1 mt-2" data-id="element-6956">
            <ArrowUpRight className="w-3 h-3" data-id="element-6957" /> +2.4% к прошлому месяцу
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200" data-id="element-6958">
          <div className="text-sm text-slate-500 mb-1" data-id="element-6959">Отток (Churn Rate)</div>
          <div className="text-3xl font-bold text-rose-600" data-id="element-6960">
            {mockAnalytics.churnRate}%
          </div>
          <div className="text-xs text-emerald-600 flex items-center gap-1 mt-2" data-id="element-6961">
            <ArrowRight className="w-3 h-3" data-id="element-6962" /> В пределах нормы
          </div>
        </Card>
        <Card padding="sm" className="border-slate-200" data-id="element-6963">
          <div className="text-sm text-slate-500 mb-1" data-id="element-6964">ARPU</div>
          <div className="text-3xl font-bold text-indigo-600" data-id="element-6965">
            {mockAnalytics.arpu} ₽
          </div>
          <div className="text-xs text-indigo-600 flex items-center gap-1 mt-2" data-id="element-6966">
            <ArrowUpRight className="w-3 h-3" data-id="element-6967" /> +150 ₽ к прошлому месяцу
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-6968">
        <Card padding="sm" className="border-slate-200" data-id="element-6969">
          <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-6970">
            Доход по планам (MRR)
          </h3>
          <div className="space-y-4" data-id="element-6971">
            {Object.entries(mockAnalytics.revenueByPlan).map(([plan, rev]) => {
            const percentage = rev / mockAnalytics.mrr * 100;
            return <div key={plan} data-id="element-6972">
                  <div className="flex justify-between text-xs mb-1" data-id="element-6973">
                    <span className="font-medium text-slate-700" data-id="element-6974">
                      {planLabels[plan as SubscriptionPlan]}
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-6975">
                      {rev.toLocaleString()} ₽
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-6976">
                    <div className="bg-indigo-500 h-2 rounded-full" style={{
                  width: `${percentage}%`
                }} data-id="element-6977" />
                  </div>
                </div>;
          })}
          </div>
        </Card>

        <Card padding="sm" className="border-slate-200" data-id="element-6978">
          <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-6979">
            Воронка конверсии
          </h3>
          <div className="space-y-4" data-id="element-6980">
            <div className="relative pt-2" data-id="element-6981">
              <div className="flex justify-between text-xs mb-1" data-id="element-6982">
                <span className="text-slate-500" data-id="element-6983">Регистрации</span>
                <span className="font-bold" data-id="element-6984">100% (5,240)</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-6 overflow-hidden" data-id="element-6985">
                <div className="bg-slate-300 h-full w-full" data-id="element-6986" />
              </div>
            </div>
            <div className="relative" data-id="element-6987">
              <div className="flex justify-between text-xs mb-1" data-id="element-6988">
                <span className="text-slate-500" data-id="element-6989">Начали Trial</span>
                <span className="font-bold" data-id="element-6990">45% (2,358)</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-6 overflow-hidden" data-id="element-6991">
                <div className="bg-blue-400 h-full w-[45%]" data-id="element-6992" />
              </div>
            </div>
            <div className="relative" data-id="element-6993">
              <div className="flex justify-between text-xs mb-1" data-id="element-6994">
                <span className="text-slate-500" data-id="element-6995">Первая оплата</span>
                <span className="font-bold text-emerald-600" data-id="element-6996">18% (943)</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-6 overflow-hidden" data-id="element-6997">
                <div className="bg-emerald-500 h-full w-[18%]" data-id="element-6998" />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>;
  return <div className="space-y-6 relative" data-id="element-6999">
      {/* Header & Tabs */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-200 pb-4" data-id="element-7000">
        <div data-id="element-7001">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-7002">
            Управление подписками
          </h2>
          <p className="text-sm text-slate-500" data-id="element-7003">
            Единая система подписок для астрологов и пользователей
          </p>
        </div>
        <div className="flex bg-slate-100 p-1 rounded-lg" data-id="element-7004">
          <button onClick={() => setActiveView('list')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeView === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-7005">
            Список
          </button>
          <button onClick={() => setActiveView('errors')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${activeView === 'errors' ? 'bg-white text-rose-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-7006">
            Ошибки{' '}
            {subscriptions.filter(s => s.activeErrors.length > 0).length > 0 && <span className="w-2 h-2 rounded-full bg-rose-500" data-id="element-7007" />}
          </button>
          <button onClick={() => setActiveView('plans')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeView === 'plans' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-7008">
            Тарифы
          </button>
          <button onClick={() => setActiveView('analytics')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeView === 'analytics' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-7009">
            Аналитика
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="relative" data-id="element-7010">
        {activeView === 'list' && renderList()}
        {activeView === 'errors' && renderErrors()}
        {activeView === 'plans' && renderPlans()}
        {activeView === 'analytics' && renderAnalytics()}

        {/* Right Panel Overlay */}
        {selectedSubId && <>
            <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40 transition-opacity" onClick={() => setSelectedSubId(null)} data-id="element-7011" />
            {renderRightPanel()}
          </>}
      </div>
    </div>;
}